import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ClipboardList, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { API_URL } from '@/services/apiClient';

interface AuditEntry {
  id: string;
  actorName: string;
  actorRole: string;
  action: string;
  targetTable: string | null;
  targetId: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const ROLE_BADGE: Record<string, { light: string; dark: string }> = {
  admin: { light: 'bg-blue-100 text-blue-700', dark: 'bg-blue-900/40 text-blue-400' },
  teacher: { light: 'bg-purple-100 text-purple-700', dark: 'bg-purple-900/40 text-purple-400' },
  super_admin: { light: 'bg-red-100 text-red-700', dark: 'bg-red-900/40 text-red-400' },
  system: { light: 'bg-slate-100 text-slate-600', dark: 'bg-slate-700 text-white' },
};

function formatDateTime(str: string) {
  return new Date(str).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function AuditLogView({ dm }: { dm: boolean }) {
  const [logs, setLogs] = useState<AuditEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 30, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const cardCls = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-white' : 'text-slate-800';
  const descCls = dm ? 'text-white' : 'text-slate-500';
  const bodyTextCls = dm ? 'text-white' : 'text-slate-700';
  const mutedCls = dm ? 'text-white' : 'text-slate-400';
  const borderCls = dm ? 'border-slate-700' : 'border-slate-200';
  const rowHover = dm ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50';
  const codeBg = dm ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700';

  const fetch_ = useCallback(async () => {
    setLoading(true);
    const token = sessionStorage.getItem('authToken');
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/audit-log?page=${page}&limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setLogs(d.logs || []);
        setPagination(d.pagination);
      }
    } catch {/* */} finally { setLoading(false); }
  }, [page]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const exportCSV = () => {
    const rows = [['Time', 'Actor', 'Role', 'Action', 'Target', 'ID']];
    logs.forEach(l => rows.push([
      l.createdAt, l.actorName, l.actorRole, l.action,
      l.targetTable || '', l.targetId || '',
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `audit-log-page${page}.csv`,
    });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className={`text-lg font-semibold ${titleCls}`}>Admin Audit Log</h2>
        <button onClick={exportCSV}
          className="btn-glass btn-glass-pill btn-glass-blue flex items-center gap-2 text-sm">
          <Download className="h-4 w-4" />Export CSV
        </button>
      </div>

      <Card className={cardCls}>
        <CardHeader className="pb-2">
          <CardTitle className={`text-base ${titleCls}`}>Activity Log</CardTitle>
          <CardDescription className={descCls}>
            {pagination.total} total entries · Page {pagination.page} of {pagination.totalPages}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : logs.length === 0 ? (
            <div className={`flex flex-col items-center justify-center h-40 ${mutedCls}`}>
              <ClipboardList className="h-10 w-10 mb-2 opacity-40" />
              <p className="text-sm">No audit log entries yet</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b text-xs font-medium uppercase tracking-wider ${borderCls} ${descCls}`}>
                      <th className="px-4 py-3 text-left">Time</th>
                      <th className="px-4 py-3 text-left">Actor</th>
                      <th className="px-4 py-3 text-left">Action</th>
                      <th className="px-4 py-3 text-left">Target</th>
                      <th className="px-4 py-3 text-center">Details</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${dm ? 'divide-slate-700' : 'divide-slate-100'}`}>
                    {logs.map(log => {
                      const roleBadge = ROLE_BADGE[log.actorRole] || ROLE_BADGE.system;
                      const isExpanded = expanded === log.id;
                      return (
                        <>
                          <tr key={log.id} className={`transition-colors cursor-pointer ${rowHover}`}
                            onClick={() => setExpanded(isExpanded ? null : log.id)}>
                            <td className="px-4 py-3">
                              <span className={`text-xs ${mutedCls}`}>{formatDateTime(log.createdAt)}</span>
                            </td>
                            <td className="px-4 py-3">
                              <p className={`text-sm font-medium ${bodyTextCls}`}>{log.actorName}</p>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full capitalize ${dm ? roleBadge.dark : roleBadge.light}`}>
                                {log.actorRole.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`font-mono text-xs ${bodyTextCls}`}>{log.action}</span>
                            </td>
                            <td className="px-4 py-3">
                              {log.targetTable ? (
                                <div>
                                  <span className={`text-xs ${bodyTextCls}`}>{log.targetTable}</span>
                                  {log.targetId && (
                                    <p className={`text-xs font-mono truncate max-w-[120px] ${mutedCls}`}>{log.targetId.slice(0, 8)}…</p>
                                  )}
                                </div>
                              ) : <span className={`text-xs ${mutedCls}`}>—</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {log.details && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${dm ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                  {isExpanded ? 'Hide' : 'Show'}
                                </span>
                              )}
                            </td>
                          </tr>
                          {isExpanded && log.details && (
                            <tr key={`${log.id}-expanded`}>
                              <td colSpan={5} className={`px-4 pb-3 ${dm ? 'bg-slate-900/40' : 'bg-slate-50'}`}>
                                <pre className={`text-xs rounded-lg p-3 overflow-auto max-h-40 ${codeBg}`}>
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className={`flex items-center justify-between px-4 py-3 border-t ${borderCls}`}>
                <span className={`text-xs ${mutedCls}`}>
                  {(page - 1) * 30 + 1}–{Math.min(page * 30, pagination.total)} of {pagination.total}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 ${dm ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
                    <ChevronLeft className={`h-4 w-4 ${mutedCls}`} />
                  </button>
                  <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page === pagination.totalPages}
                    className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 ${dm ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
                    <ChevronRight className={`h-4 w-4 ${mutedCls}`} />
                  </button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
